/* eslint-disable react/jsx-curly-brace-presence */
import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import AvatarGroup from '@atlaskit/avatar-group/avatar-group';
import type { AvatarProps } from '@atlaskit/avatar-group/types';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Pressable from '@atlaskit/primitives/pressable';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import { Popup } from '@atlaskit/popup/popup';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { usePageSpaceKey } from '@confluence/page-context';
import { ShareAndRestrictDialogController } from '@confluence/share-and-restrict-dialog/entry-points/ShareAndRestrictDialogController.ts';
import { fg } from '@confluence/feature-gating';
import { TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component.ts';
import { useIsGuest } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { isExternalShareRequest } from '@confluence/external-share-utils/entry-points/isExternalShareRequest.ts';
import { LoadableAfterPaint } from '@confluence/loadable';

import type { AgentWithPresence } from '../AgentPresence/AgentPresenceTypes.ts';
import { AgentPresenceAvatar } from '../AgentPresence/AgentPresenceAvatar.tsx';
import { MAX_GROUP_SIZE } from '../presenceUtils.ts';
import type { Participant, PresenceContentMode, PresenceContentType } from '../presenceTypes.ts';

import { PresenceAvatarButton } from './PresenceAvatarButton.tsx';

const NoCollaboratorsDialog = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-NoCollaboratorsDialog" */ './NoCollaboratorsDialog.tsx'
			)
		).NoCollaboratorsDialog,
});

const PresenceMenuWithGuest = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-PresenceMenuWithGuest" */ './PresenceMenuWithGuest.tsx'
			)
		).PresenceMenuWithGuest,
});

const i18n = defineMessages({
	overflowLabel: {
		id: 'team-presence.avatar-group.overflow-label',
		defaultMessage: 'Overflow menu',
		description: 'Accessible label for presence avatar group overflow menu',
	},
	blogpostLandmark: {
		id: 'team-presence.avatar-group.blogpost-landmark',
		defaultMessage: 'Active people on blog post',
		description: 'Accessible region landmark for presence avatar group when on a blog post',
	},
	pageLandmark: {
		id: 'team-presence.avatar-group.page-landmark',
		defaultMessage: 'Active people on page',
		description: 'Accessible region landmark for presence avatar group when on a page',
	},
	agentEditingTooltip: {
		id: 'team-presence.avatar-group.agent-editing-tooltip',
		defaultMessage: '{name} (Editing)',
		description: 'Tooltip text shown for an AI agent presence avatar when the agent is editing.',
	},
	agentViewingTooltip: {
		id: 'team-presence.avatar-group.agent-viewing-tooltip',
		defaultMessage: '{name} (Viewing)',
		description: 'Tooltip text shown for an AI agent presence avatar when the agent is viewing.',
	},
});

export type NestedAgentPresenceChildProps = {
	isNestedInPresenceAvatarGroup?: boolean;
	onActiveAgentsChange?: (agents: AgentWithPresence[]) => void;
};

export type NestedAgentPresenceChild = React.ReactElement<NestedAgentPresenceChildProps>;

export type PresenceAvatarGroupProps = {
	contentId: string;
	contentMode: PresenceContentMode;
	contentType: PresenceContentType;
	participants: Participant[];
	analyticSource?: string;
	popupOpen?: boolean;
	totalParticipants: number;
	children?: NestedAgentPresenceChild;
};

export type ParticipantAvatarItem = Participant & {
	itemType: 'participant';
	participant: Participant;
};

export type AgentAvatarItem = AvatarProps & {
	itemType: 'agent';
	agent: AgentWithPresence;
};

export type PresenceAvatarItem = ParticipantAvatarItem | AgentAvatarItem;

export const isAgentAvatarItem = (item: PresenceAvatarItem): item is AgentAvatarItem =>
	item.itemType === 'agent';

export const getPresenceAvatarItems = (
	participants: Participant[],
	agents: AgentWithPresence[],
): PresenceAvatarItem[] => [
	...participants.map((participant) => ({
		...participant,
		itemType: 'participant' as const,
		participant,
	})),
	...agents.map((agent) => ({
		itemType: 'agent' as const,
		agent,
		name: agent.name,
	})),
];

export const getPresenceAvatarItemAtIndex = (
	avatarItems: PresenceAvatarItem[],
	index: number,
): PresenceAvatarItem => avatarItems[index];

export const canShowEmptyPresenceDialog = ({
	totalPresenceItems,
	participantCount,
	isLoggedIn,
	isGuest,
	isExternal,
}: {
	totalPresenceItems: number;
	participantCount: number;
	isLoggedIn: boolean;
	isGuest: boolean;
	isExternal: boolean;
}): boolean => {
	return (
		totalPresenceItems === 1 && participantCount === 1 && isLoggedIn && !isGuest && !isExternal
	);
};

export const getNestedPresenceContent = ({
	isAgentPresenceMoveEnabled,
	children,
	onActiveAgentsChange,
}: {
	isAgentPresenceMoveEnabled: boolean;
	children?: NestedAgentPresenceChild;
	onActiveAgentsChange: (agents: AgentWithPresence[]) => void;
}): React.ReactElement<
	NestedAgentPresenceChildProps,
	string | React.JSXElementConstructor<any>
> | null => {
	if (!isAgentPresenceMoveEnabled || !React.isValidElement(children)) {
		return null;
	}

	return React.cloneElement(children, {
		isNestedInPresenceAvatarGroup: true,
		onActiveAgentsChange,
	});
};

const EMPTY_AGENTS: AgentWithPresence[] = [];

const popupContainerStyles = xcss({
	backgroundColor: 'elevation.surface.overlay',
	boxShadow: 'elevation.shadow.overlay',
	borderRadius: 'radius.small',
	position: 'relative',
	width: '360px',
});

const editObjectHeaderStyles = xcss({
	paddingTop: 'space.0',
	paddingBottom: 'space.0',
});

const activePeopleRegionStyles = xcss({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
});

const overflowMoreStyles = xcss({
	padding: 'space.075',
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- Requested suppression for existing token string
	backgroundColor: 'color.background.accent.gray.subtler',
	borderRadius: 'radius.full',
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- Requested suppression for existing token string
	color: 'color.text.subtle',
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- Requested suppression for existing token string
	font: 'font.heading.xxsmall',
	':hover': {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- Requested suppression for existing token string
		backgroundColor: 'color.background.accent.gray.subtler.hovered',
	},
	':active': {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- Requested suppression for existing token string
		backgroundColor: 'color.background.accent.gray.subtler.pressed',
	},
});

const moreActiveStyles = xcss({
	backgroundColor: 'color.background.selected',
	color: 'color.text.selected',
	':hover': {
		backgroundColor: 'color.background.selected.hovered',
	},
	':active': {
		backgroundColor: 'color.background.selected.pressed',
	},
});

export const getAgentPresenceTooltipMessage = (
	agent: AgentWithPresence,
): {
	id: string;
	defaultMessage: string;
	description: string;
} => (agent.presenceActivity === 'editor' ? i18n.agentEditingTooltip : i18n.agentViewingTooltip);

// Base styles for indicator - margin will be conditionally applied based on feature gate
const indicatorBaseStyles = xcss({
	minWidth: '28px',
	backgroundColor: 'color.background.neutral.subtle',
	padding: 'space.0',
	borderRadius: 'radius.small',
	':focus-visible': {
		outlineColor: 'color.border.focused',
		outlineStyle: 'solid',
		outlineWidth: 'border.width.focused',
		outlineOffset: 'space.050',
	},
});

// Legacy margin styles (used when platform spacing fix is disabled)
const indicatorLegacyMarginStyles = xcss({
	margin: 'space.025',
});

const indicatorOverflowStyles = xcss({
	minWidth: '35px',
	maxWidth: '35px',
});

export const PresenceAvatarGroup = ({
	contentId,
	contentMode,
	contentType,
	analyticSource,
	popupOpen,
	participants,
	totalParticipants,
	children,
}: PresenceAvatarGroupProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { isLoggedIn } = useSessionData();
	const isGuest = useIsGuest();
	const isExternal = isExternalShareRequest();
	const [spaceKey] = usePageSpaceKey();

	const [isPopupOpen, setIsPopupOpen] = useState(popupOpen || false);
	const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
	const [activeAgents, setActiveAgents] = useState<AgentWithPresence[]>([]);
	const combinedPresenceAgents = fg('platform_move_presence_agents') ? activeAgents : EMPTY_AGENTS;

	const avatarItems = useMemo(
		() => getPresenceAvatarItems(participants, combinedPresenceAgents),
		[combinedPresenceAgents, participants],
	);
	const totalPresenceItems = totalParticipants + combinedPresenceAgents.length;

	const isEligibleForEmptyPresenceDialog = useCallback(() => {
		// only 1 user, not anonymous and not guest user
		return canShowEmptyPresenceDialog({
			totalPresenceItems,
			participantCount: participants.length,
			isLoggedIn,
			isGuest,
			isExternal,
		});
	}, [participants.length, totalPresenceItems, isLoggedIn, isGuest, isExternal]);

	useEffect(() => {
		if (isPopupOpen && totalPresenceItems > 1 && totalPresenceItems <= MAX_GROUP_SIZE) {
			setIsPopupOpen(false);
		}
	}, [isPopupOpen, totalPresenceItems]);

	const handleShareClick = useCallback(
		(analyticsTriggerSource: string) => {
			setIsPopupOpen(false);
			const shareDialogType = 'v2';
			setIsShareDialogOpen(true);
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: analyticsTriggerSource,
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'presenceShare',
					attributes: {
						contentId,
						contentMode,
						contentType,
						shareDialog: shareDialogType,
					},
				},
			}).fire();
		},
		[contentId, contentMode, contentType, createAnalyticsEvent],
	);

	const toggleEmptyPresenceDialog = useCallback(() => {
		setIsPopupOpen((isPopupOpenState) => !isPopupOpenState);

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: analyticSource,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'singlePresenceAvatarGroup',
				product: 'confluence',
				attributes: {
					contentId,
					contentMode,
					contentType,
					toggle: isPopupOpen ? 'close' : 'open',
				},
			},
		}).fire();
	}, [contentId, contentMode, contentType, analyticSource, createAnalyticsEvent, isPopupOpen]);

	const isSingleParticipant = totalPresenceItems === 1;

	const onMoreClick = useCallback(
		(e: React.MouseEvent<Element, MouseEvent>, number: number) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: analyticSource,
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'presenceAvatarGroupMoreButton',
					product: 'confluence',
					attributes: {
						contentId,
						contentType,
						contentMode,
						number,
					},
				},
			}).fire();

			setIsPopupOpen((isPopupOpenState) => !isPopupOpenState);

			// platform-design-system-popup-ref causes the popup to close when the user clicks on the more button,
			// revisit what other types of changes we can put in place to not have to call stopPropagation
			e.stopPropagation();
		},
		[contentId, contentType, contentMode, analyticSource, createAnalyticsEvent],
	);

	const getTriggerId = () => {
		return isSingleParticipant ? 'no-collaborators-dialog' : 'presence-menu';
	};

	const triggerId = getTriggerId();

	const renderNoCollaboratorsDialog = () => (
		<NoCollaboratorsDialog
			contentId={contentId}
			contentMode={contentMode}
			contentType={contentType}
			spaceKey={spaceKey || ''}
			onShareClick={handleShareClick}
			testId={triggerId}
		/>
	);

	const renderPresenceMenu = () => (
		<PresenceMenuWithGuest
			contentId={contentId}
			contentType={contentType}
			contentMode={contentMode}
			participants={participants}
			agents={combinedPresenceAgents}
			totalParticipants={totalPresenceItems}
		/>
	);

	const getPopUpContent = () => {
		return isSingleParticipant ? renderNoCollaboratorsDialog() : renderPresenceMenu();
	};

	const PopupContent = getPopUpContent();

	const overflowParticipantCount = totalPresenceItems - MAX_GROUP_SIZE + 1;

	const landmarkRegionLabel =
		contentType === 'blogpost'
			? formatMessage(i18n.blogpostLandmark)
			: formatMessage(i18n.pageLandmark);

	const isEditObjectHeader = contentMode === 'edit' || contentMode === 'live';
	const nestedPresenceContent = getNestedPresenceContent({
		isAgentPresenceMoveEnabled: fg('platform_move_presence_agents'),
		children,
		onActiveAgentsChange: setActiveAgents,
	});

	const renderAvatar = (_: unknown, props: AvatarProps, index: number) => {
		const avatarItem = getPresenceAvatarItemAtIndex(avatarItems, index);

		if (!avatarItem) {
			return null;
		}

		if (isAgentAvatarItem(avatarItem)) {
			const agentTooltipMessage = getAgentPresenceTooltipMessage(avatarItem.agent);

			return (
				<React.Fragment key={`AgentPresenceAvatar-${avatarItem.agent.id}`}>
					<Tooltip
						content={formatMessage(agentTooltipMessage, { name: avatarItem.agent.name })}
						position="bottom"
					>
						<AgentPresenceAvatar
							agent={avatarItem.agent}
							presenceColorKey={avatarItem.agent.presenceId}
							size="small"
							stackIndex={MAX_GROUP_SIZE - index}
							showPresenceBadge
							showBorder
						/>
					</Tooltip>
				</React.Fragment>
			);
		}

		return (
			<React.Fragment key={`PresenceAvatarButton-${avatarItem.participant.id}`}>
				<PresenceAvatarButton
					index={index}
					participant={avatarItem.participant}
					contentMode={contentMode}
					contentType={contentType}
					contentId={contentId}
					isSingleParticipant={isSingleParticipant}
					isEligibleForEmptyPresenceDialog={isEligibleForEmptyPresenceDialog()}
					toggleEmptyPresenceDialog={toggleEmptyPresenceDialog}
					avatarProps={props}
					analyticSource={analyticSource}
				/>
			</React.Fragment>
		);
	};

	return (
		<>
			<ExperienceSuccess name={TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE} />
			{participants.length === 1 && isShareDialogOpen && (
				<ShareAndRestrictDialogController
					contentId={contentId}
					contentType={contentType}
					allowRestrictions
					pageModes="view"
					refetchButtonData={() => {}}
					onClose={() => {
						setIsShareDialogOpen(false);
					}}
					isUnpublishedDraft={false}
				/>
			)}
			<Popup
				isOpen={isPopupOpen}
				onClose={() => setIsPopupOpen(false)}
				placement="bottom-end"
				content={() => <Box xcss={popupContainerStyles}>{PopupContent}</Box>}
				trigger={(triggerProps) => (
					<Box
						{...triggerProps}
						paddingInline="space.050"
						aria-label={landmarkRegionLabel}
						role="region"
						xcss={[
							fg('platform_move_presence_agents') && activePeopleRegionStyles,
							isEditObjectHeader && editObjectHeaderStyles,
						]}
						data-vc="presence-avatar-group"
					>
						<AvatarGroup
							appearance="stack"
							size="small"
							maxCount={MAX_GROUP_SIZE}
							data={avatarItems}
							testId="presence-avatar-group"
							label={landmarkRegionLabel}
							borderColor={token('color.background.neutral')}
							onMoreClick={(e) => onMoreClick(e, overflowParticipantCount)}
							shouldPopupRenderToParent={false}
							isTooltipDisabled
							overrides={{
								Avatar: {
									render: renderAvatar,
								},
								MoreIndicator: {
									render: (_, { buttonProps, isActive: __, borderColor: ___, ...rest }) => (
										<Pressable
											type="submit"
											{...buttonProps}
											{...rest}
											padding={fg('platform-avatar-group-spacing-fix') ? 'space.0' : 'space.025'}
											onClick={rest.onClick}
											xcss={[
												indicatorBaseStyles,
												!fg('platform-avatar-group-spacing-fix') && indicatorLegacyMarginStyles,
												// TODO: This is a bug, anything above 1000 will break...
												overflowParticipantCount > 100 && indicatorOverflowStyles,
											]}
											testId={rest.testId}
											data-vc="presence-overflow"
											aria-label={formatMessage(i18n.overflowLabel)}
										>
											<Box xcss={[overflowMoreStyles, isPopupOpen && moreActiveStyles]}>
												{overflowParticipantCount}
											</Box>
										</Pressable>
									),
								},
							}}
						/>
						{nestedPresenceContent}
					</Box>
				)}
			/>
		</>
	);
};
